<template>
  <div class="class-manager">
    <!-- 搜索区域 -->
    <div class="search-area">
      <div class="search-row">
        <div class="search-item">
          <span class="label">班级名称:</span>
          <el-input v-model="searchForm.className" placeholder="请输入班级名称" clearable />
        </div>
        <div class="search-item">
          <span class="label">所属学院:</span>
          <el-select v-model="searchForm.collegeName" placeholder="请选择学院" clearable>
            <el-option v-for="item in collegeOptions" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </div>
        <div class="search-item">
          <span class="label">专业名称:</span>
          <el-input v-model="searchForm.majorName" placeholder="请输入专业名称" clearable />
        </div>
      </div>
      <div class="search-row">
        <div class="search-item">
          <span class="label">导员姓名:</span>
          <el-input v-model="searchForm.counselorName" placeholder="请输入导员姓名" clearable />
        </div>
        <div class="search-item">
          <span class="label">班级人数:</span>
          <el-input v-model="searchForm.studentCountMin" placeholder="最小人数" style="width: 100px" />
          <span class="separator">至</span>
          <el-input v-model="searchForm.studentCountMax" placeholder="最大人数" style="width: 100px" />
        </div>
        <div class="search-item">
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="handleReset">重置</el-button>
          <el-button type="success" @click="handleAdd">新增班级</el-button>
        </div>
      </div>
    </div>

    <!-- 统计信息 -->
    <div class="statistics">
      <span>共 {{ total }} 条</span>
    </div>

    <!-- 新增班级弹窗组件 -->
    <AddClassDialog v-model="dialogVisible" :existing-classes="tableData" @success="handleAddSuccess" />

    <!-- 表格区域 -->
    <div class="table-area">
      <el-table
        v-loading="loading"
        :data="tableData"
        border
        stripe
        style="width: 100%"
        height="calc(100vh - 280px)"
        @selection-change="handleSelectionChange"
      >
        <el-table-column type="selection" width="40" align="center" />
        <el-table-column type="index" label="序号" width="60" align="center" :index="indexMethod" />
        <el-table-column prop="className" label="班级名称" min-width="180" sortable />
        <el-table-column prop="majorName" label="专业名称" width="150" sortable />
        <el-table-column prop="collegeName" label="所属学院" width="180" sortable />
        <el-table-column prop="studentCount" label="班级人数" width="100" sortable align="center" />
        <el-table-column prop="counselorName" label="导员姓名" width="120" sortable />
        <el-table-column prop="counselorPhone" label="导员电话" width="140" />
        <el-table-column label="操作" width="180" fixed="right" align="center">
          <template #default="{ row }">
            <el-button type="primary" size="small" @click="handleViewDetail(row)">浏览数据</el-button>
            <el-button type="danger" size="small" @click="handleDelete(row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 底部操作栏 -->
    <div class="bottom-area">
      <div class="left-actions">
        <el-dropdown @command="handleExport">
          <el-button>导出Excel <el-icon class="el-icon--right"><arrow-down /></el-icon></el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item command="selected">导出勾选</el-dropdown-item>
              <el-dropdown-item command="current">导出当前页</el-dropdown-item>
              <el-dropdown-item command="all">导出全部</el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>
      <div class="pagination-wrapper">
        <span class="total-text">共 {{ total }} 条</span>
        <el-select v-model="pageSize" style="width: 80px; margin: 0 10px" @change="handleSizeChange">
          <el-option label="10条/页" :value="10" />
          <el-option label="20条/页" :value="20" />
          <el-option label="30条/页" :value="30" />
          <el-option label="50条/页" :value="50" />
        </el-select>
        <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="total"
          layout="prev, pager, next"
          background
          @current-change="handleCurrentChange"
        />
        <span class="goto-text">前往</span>
        <el-input v-model="gotoPage" style="width: 50px" @blur="handleGotoPage" />
        <span>页</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import request from '@/utils/request'
import { ElMessage } from 'element-plus'
import { ArrowDown } from '@element-plus/icons-vue'
import AddClassDialog from './AddClassDialog.vue'

export default {
  name: 'ClassManager',
  components: {
    AddClassDialog,
    ArrowDown
  },
  setup() {
    const router = useRouter()
    
    // 搜索表单
    const searchForm = reactive({
      className: '',
      collegeName: '',
      majorName: '',
      counselorName: '',
      studentCountMin: '',
      studentCountMax: ''
    })

    // 学院选项
    const collegeOptions = [
      { value: '电子与信息工程学院', label: '电子与信息工程学院' },
      { value: '管理学院', label: '管理学院' },
      { value: '土木学院', label: '土木学院' },
      { value: '机械学院', label: '机械学院' },
      { value: '艺术学院', label: '艺术学院' }
    ]

    // 分页相关
    const currentPage = ref(1)
    const pageSize = ref(30)
    const gotoPage = ref(1)
    const total = ref(0)

    // 表格数据
    const tableData = ref([])
    const loading = ref(false)

    // 选中项
    const selectedItems = ref([])

    // 新增班级弹窗显示状态
    const dialogVisible = ref(false)

    // 获取班级列表数据
    const fetchClassList = async () => {
      loading.value = true
      try {
        const params = {
          pageNum: currentPage.value,
          pageSize: pageSize.value,
          className: searchForm.className,
          collegeName: searchForm.collegeName,
          majorName: searchForm.majorName,
          counselorName: searchForm.counselorName,
          studentCountMin: searchForm.studentCountMin,
          studentCountMax: searchForm.studentCountMax
        }
        const response = await request.post('/class/list', params)
        if (response.data && response.data.code === 200) {
          tableData.value = response.data.data.list || []
          total.value = response.data.data.total || 0
        }
      } catch (error) {
        console.error('获取班级列表失败:', error)
      } finally {
        loading.value = false
      }
    }

    // 计算序号
    const indexMethod = (index) => {
      return (currentPage.value - 1) * pageSize.value + index + 1
    }

    // 搜索
    const handleSearch = () => {
      currentPage.value = 1
      fetchClassList()
    }

    // 重置
    const handleReset = () => {
      searchForm.className = ''
      searchForm.collegeName = ''
      searchForm.majorName = ''
      searchForm.counselorName = ''
      searchForm.studentCountMin = ''
      searchForm.studentCountMax = ''
      currentPage.value = 1
      fetchClassList()
    }

    // 页码变化
    const handleCurrentChange = (val) => {
      currentPage.value = val
      fetchClassList()
    }

    // 每页条数变化
    const handleSizeChange = (val) => {
      pageSize.value = val
      currentPage.value = 1
      fetchClassList()
    }

    // 选中变化
    const handleSelectionChange = (selection) => {
      selectedItems.value = selection
    }

    // 跳转到学生数据浏览器
    const handleViewDetail = (row) => {
      // 使用路由跳转，传递班级完整信息（包括专业代码和学年，用于过滤专业课）
      router.push({
        path: '/student-browser',
        query: {
          classCode: row.classCode,
          className: row.className,
          majorCode: row.majorCode,
          academicYear: row.academicYear
        }
      })
    }

    // 删除班级
    const handleDelete = (row) => {
      console.log('删除班级:', row)
    }

    // 跳转到指定页
    const handleGotoPage = () => {
      const page = parseInt(gotoPage.value)
      const maxPage = Math.ceil(total.value / pageSize.value)
      if (page && page > 0 && page <= maxPage) {
        currentPage.value = page
        fetchClassList()
      }
    }

    // 打开新增弹窗
    const handleAdd = () => {
      dialogVisible.value = true
    }

    // 新增成功回调
    const handleAddSuccess = () => {
      fetchClassList()
    }

    // 处理导出
    const handleExport = async (command) => {
      // 准备导出数据
      let exportData = []
      
      if (command === 'selected') {
        // 导出勾选
        if (selectedItems.value.length === 0) {
          ElMessage.warning('请先勾选要导出的班级')
          return
        }
        exportData = selectedItems.value
      } else if (command === 'current') {
        // 导出当前页
        exportData = tableData.value
      } else if (command === 'all') {
        // 导出全部 - 需要查询所有数据
        try {
          const params = {
            pageNum: 1,
            pageSize: 9999,  // 获取全部
            className: searchForm.className,
            collegeName: searchForm.collegeName,
            majorName: searchForm.majorName,
            counselorName: searchForm.counselorName,
            studentCountMin: searchForm.studentCountMin,
            studentCountMax: searchForm.studentCountMax
          }
          const response = await request.post('/class/list', params)
          if (response.data && response.data.code === 200) {
            exportData = response.data.data.list || []
          }
        } catch (error) {
          console.error('获取全部数据失败:', error)
          ElMessage.error('导出失败')
          return
        }
      }

      if (exportData.length === 0) {
        ElMessage.warning('没有数据可导出')
        return
      }

      try {
        // 调用后端导出接口
        const params = {
          exportType: command,
          classList: exportData
        }
        const response = await request.post('/class/export', params, {
          responseType: 'blob'
        })

        // 下载文件
        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = `班级数据_${new Date().getTime()}.xlsx`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        ElMessage.success('导出成功')
      } catch (error) {
        console.error('导出失败:', error)
        ElMessage.error('导出失败：' + error.message)
      }
    }

    // 组件挂载时获取数据
    onMounted(() => {
      fetchClassList()
    })

    return {
      searchForm,
      collegeOptions,
      currentPage,
      pageSize,
      gotoPage,
      total,
      tableData,
      loading,
      indexMethod,
      handleSearch,
      handleReset,
      handleCurrentChange,
      handleSizeChange,
      handleSelectionChange,
      handleViewDetail,
      handleDelete,
      handleGotoPage,
      dialogVisible,
      handleAdd,
      handleAddSuccess,
      handleExport,
      selectedItems
    }
  }
}
</script>

<style scoped src="./ClassManager.css"></style>
